/**
 * Where to send someone after they sign in.
 *
 * `returnTo` arrives in the query string, so anyone can write it: a link that
 * signs you in and then drops you on a lookalike site is a phishing kit. Only
 * two shapes are honoured:
 *
 *   - a path on this site (`/library/42`), which the router handles, and
 *   - an https URL on a squarebitstudios.com subdomain, which is how apps that
 *     live on their own host - Riser, at riser.squarebitstudios.com - send
 *     people here to sign in and get them back. The session cookie is issued
 *     on `.squarebitstudios.com`, so it is valid there the moment this returns.
 *
 * Anything else, including protocol-relative `//host` paths, is ignored.
 */
export type ReturnTarget =
  | { kind: 'route'; path: string }
  | { kind: 'external'; url: string }
  | null;

const SITE_DOMAIN = 'squarebitstudios.com';

export function resolveReturnTo(value: string | null | undefined): ReturnTarget {
  if (!value) return null;

  if (value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')) {
    return { kind: 'route', path: value };
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  if (
    url.protocol === 'https:' &&
    !url.username &&
    !url.password &&
    (host === SITE_DOMAIN || host.endsWith(`.${SITE_DOMAIN}`))
  ) {
    return { kind: 'external', url: url.toString() };
  }
  return null;
}
